import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './PlainModal.module.scss';

export type PlainModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface PlainModalProps {
  isOpen: boolean;
  children: React.ReactNode;
  onClose?: () => void;
  ariaLabel?: string;
  id?: string;
  dataTestId?: string;
  contentDesc?: string;
  size?: PlainModalSize;
  rootClassName?: string;
  backdropClassName?: string;
  dialogClassName?: string;
  disableBackdropClose?: boolean;
  disableEscapeKeyDown?: boolean;
  lockScroll?: boolean;
}

const getSizeClass = (size: PlainModalSize) => {
  switch (size) {
    case 'xs':
      return styles.sizeXs;
    case 'md':
      return styles.sizeMd;
    case 'lg':
      return styles.sizeLg;
    case 'xl':
      return styles.sizeXl;
    case 'sm':
    default:
      return styles.sizeSm;
  }
};

const PlainModal: React.FC<PlainModalProps> = ({
  isOpen,
  children,
  onClose,
  ariaLabel,
  id,
  dataTestId,
  contentDesc,
  size = 'sm',
  rootClassName,
  backdropClassName,
  dialogClassName,
  disableBackdropClose = false,
  disableEscapeKeyDown = false,
  lockScroll = true,
}) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen || disableEscapeKeyDown || !onClose) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [disableEscapeKeyDown, isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !lockScroll) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isOpen, lockScroll]);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const closeFromBackdrop = () => {
    if (!disableBackdropClose) {
      onClose?.();
    }
  };

  const automationLabel = contentDesc || ariaLabel || dataTestId || id;
  const dialogLocatorProps = {
    'data-testid': dataTestId,
    'content-desc': automationLabel,
  } as React.HTMLAttributes<HTMLDivElement> & { 'content-desc'?: string };

  return (
    <div className={clsx(styles.root, rootClassName)}>
      <div
        className={clsx(styles.backdrop, backdropClassName)}
        onClick={closeFromBackdrop}
        aria-hidden="true"
        data-testid={dataTestId ? `${dataTestId}-backdrop` : undefined}
      />
      <div
        ref={dialogRef}
        id={id}
        className={clsx(styles.dialog, getSizeClass(size), dialogClassName)}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || automationLabel}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        {...dialogLocatorProps}
      >
        {children}
      </div>
    </div>
  );
};

export default PlainModal;
